import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Link2, Search, X } from 'lucide-react';
import { apiClient } from '../../api';
import { monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, errMessage } from '../UI/ink';
import { parseTs } from '../SecurityAlerts/model';
import VerdictBadge from '../Triage/VerdictBadge';
import {
  MITRE, NEXT, SEV_LABEL, STAGES, STAGE_LABEL, STAGE_TIME, evidenceRows, mitreUrl, parseNotes,
  sevBg, sevDotBorder, sevFg, sevKey,
} from './model';

const Label = ({ children, right }) => (
  <span className="flex items-center justify-between gap-3">
    <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>
    {right}
  </span>
);

export const SevChip = ({ severity, big }) => {
  const s = sevKey(severity);
  return (
    <span className={`inline-flex items-center gap-1.5 ${big ? 'h-6 px-[9px] text-[11px]' : 'h-[22px] px-2 text-[10.5px]'} rounded-md font-mono font-medium tracking-[0.06em]`} style={{ background: sevBg(s) }}>
      <span className={`${big ? 'w-[7px] h-[7px]' : 'w-1.5 h-1.5'} rounded-[1px] box-border`} style={{ background: sevFg(s), border: `1px solid ${sevDotBorder(s)}` }} />
      {SEV_LABEL[s].toUpperCase()}
    </span>
  );
};

const clock = (iso) => {
  const d = parseTs(iso);
  if (!d) return '';
  const sameDay = new Date().toDateString() === d.toDateString();
  return d.toLocaleString('en-GB', sameDay
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

const CaseLinker = ({ finding, onClose, onNewCase, onLinked }) => {
  const [cases, setCases] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get('/api/cases?limit=100')
      .then(res => {
        const open = (res.data.cases || []).filter(c => !['closed', 'resolved'].includes(c.status));
        setCases(open);
        if (open[0]) setPicked(open[0].id);
      })
      .catch(() => setCases([]));
  }, []);

  const link = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/api/cases/${picked}/findings`, { finding_id: finding.id });
      onLinked(cases.find(c => c.id === picked));
    } catch (err) {
      setError(errMessage(err, 'Could not link the finding.'));
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };

  return (
    <div role="dialog" aria-label="Link to a case" onKeyDown={onKey} className="absolute left-3.5 right-3.5 bottom-[84px] z-10 flex flex-col gap-2 p-3.5 rounded-xl border max-h-[60%]" style={{ background: 'var(--surf-raised)', borderColor: 'var(--accent)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
      <span className="flex justify-between items-center">
        <span className="text-sm font-semibold">Link to an open case</span>
        <button type="button" onClick={onClose} aria-label="Close" className="w-[30px] h-[30px] flex items-center justify-center" style={{ color: 'var(--ink-muted)' }}><X className="w-[13px] h-[13px]" strokeWidth={2} /></button>
      </span>
      <div className="flex flex-col gap-2 overflow-y-auto min-h-0">
        {cases === null && <span className="text-[13px] py-2" style={{ color: 'var(--ink-faint)' }}>Loading cases…</span>}
        {cases?.length === 0 && <span className="text-[13px] py-2" style={{ color: 'var(--ink-muted)' }}>No open cases. Start one from this finding instead.</span>}
        {cases?.map(c => (
          <label key={c.id} className="flex items-center gap-2.5 px-3 py-[9px] rounded-[9px] border cursor-pointer" style={{ borderColor: c.id === picked ? 'var(--accent)' : 'var(--surf-line)', background: 'var(--surf-surface)' }}>
            <input type="radio" name="link-case" checked={c.id === picked} onChange={() => setPicked(c.id)} className="m-0 w-[15px] h-[15px] shrink-0" style={{ accentColor: 'var(--accent)' }} />
            <span className="flex-grow min-w-0 flex flex-col gap-0.5">
              <span className="text-[13px] font-medium truncate">{c.title}</span>
              <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{c.id.slice(0, 8).toUpperCase()} · {c.status} · {c.priority}</span>
            </span>
          </label>
        ))}
      </div>
      {error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
      <span className="flex items-center gap-2.5 pt-1">
        {cases?.length > 0 && (
          <button type="button" onClick={link} disabled={busy || !picked} className="h-[38px] px-3.5 rounded-[9px] text-[13.5px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>
            {busy ? 'Linking…' : 'Link to case'}
          </button>
        )}
        <button type="button" onClick={onNewCase} className="text-[13px] underline-offset-2 hover:underline" style={{ color: 'var(--ink-link)' }}>New case from this finding</button>
      </span>
    </div>
  );
};

const FindingDetail = ({ finding, rule, members, me, busy, onClose, onUpdate, onNewCase, onNotice, verdict, canRate, onVerdictChange }) => {
  const [raw, setRaw] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);

  useEffect(() => { setLinkOpen(false); setDraft(''); }, [finding?.id]);

  if (!finding) {
    return (
      <aside aria-label="Finding details" className="h-full min-h-[520px] rounded-[14px] border flex flex-col items-center justify-center gap-2.5 p-8 text-center" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <Search className="w-7 h-7" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.6} />
        <span className="text-[15px] font-semibold">Select a finding</span>
        <span className="text-[13px] leading-normal max-w-[280px]" style={{ color: 'var(--ink-muted)' }}>What fired, the evidence, and the next step show here.</span>
      </aside>
    );
  }

  const f = finding;
  const idx = STAGES.indexOf(f.status);
  const [nextStatus, nextLabel] = NEXT[f.status] || NEXT.new;
  const notes = parseNotes(f.notes);
  const evidence = f.evidence && typeof f.evidence === 'object' ? f.evidence : {};
  const evRows = evidenceRows(evidence);
  const hints = Array.isArray(evidence.false_positive_hints) ? evidence.false_positive_hints.filter(Boolean) : [];
  const mitre = Array.isArray(f.mitre_techniques) ? f.mitre_techniques : [];
  const corr = Array.isArray(f.correlated_alerts) ? f.correlated_alerts.filter(a => a && a.id != null) : [];
  const ruleId = f.rule_id || evidence.rule_id;
  const host = f.server_hostname || f.server_id;
  const assignee = f.assigned_to || '';
  const myEmail = me?.email || '';
  const known = members.some(m => m.email === assignee);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    const next = [...notes, { who: me?.name || myEmail || null, email: myEmail || null, at: new Date().toISOString(), text }];
    const ok = await onUpdate({ notes: JSON.stringify(next) });
    if (ok) setDraft('');
  };

  return (
    <aside aria-label="Finding details" className="relative h-full min-h-[520px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-col gap-2.5 px-[22px] pt-[18px] pb-3.5 border-b shrink-0" style={{ borderColor: 'var(--surf-line)' }}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10.5px] tracking-[0.12em] truncate" style={{ color: 'var(--ink-link)' }}>FINDING · {f.id.toUpperCase()}</span>
          <button type="button" onClick={onClose} aria-label="Close details" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <X className="w-[13px] h-[13px]" strokeWidth={2} />
          </button>
        </div>
        <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em] leading-tight break-words">{f.title}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <SevChip severity={f.severity} big />
          {f.source && <span className="h-6 inline-flex items-center px-2 rounded-md border font-mono text-[10.5px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{String(f.source).toUpperCase()}</span>}
          {f.server_id && (Number(f.asset_deleted)
            ? <span className="font-mono text-xs" style={{ color: 'var(--ink-faint)' }} title="This asset was deleted. The finding is kept.">{host} · asset deleted</span>
            : <Link to={`/assets/${f.server_id}`} className="font-mono text-xs hover:underline" style={{ color: 'var(--ink-link)' }}>{host}</Link>)}
        </div>
        {verdict && (
          <div className="mt-3">
            <div className="font-mono text-[10px] tracking-[0.08em] mb-1" style={{ color: 'var(--ink-faint)' }}>AI TRIAGE</div>
            <VerdictBadge verdict={verdict} interactive canRate={canRate} onChange={onVerdictChange} />
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-[22px] py-3.5 flex flex-col gap-3.5">
        {/* Lifecycle */}
        <div className="grid grid-cols-5 gap-1" aria-label={`Stage: ${STAGE_LABEL[f.status] || f.status}`}>
          {STAGES.map((st, i) => {
            const col = STAGE_TIME[st];
            const time = col ? clock(f[col]) : '';
            return (
              <div key={st} className="flex flex-col gap-[5px] min-w-0">
                <span className="h-1 rounded-sm" style={{ background: i < idx ? 'var(--ink-muted)' : i === idx ? 'var(--accent)' : 'var(--surf-line)' }} />
                <span className="text-[11.5px] truncate" style={{ fontWeight: i === idx ? 600 : 400, color: i === idx ? 'var(--ink-text)' : i < idx ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>{STAGE_LABEL[st]}</span>
                <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{i <= idx ? time : ''}</span>
              </div>
            );
          })}
        </div>

        {/* Assignee */}
        <div className="flex items-center gap-2.5">
          <label htmlFor="finding-assignee" className="text-[12.5px] w-16 shrink-0" style={{ color: 'var(--ink-faint)' }}>Assignee</label>
          <select
            id="finding-assignee"
            value={assignee}
            disabled={busy}
            onChange={e => onUpdate({ assigned_to: e.target.value || null })}
            className="flex-grow min-w-0 h-[38px] box-border px-2.5 rounded-[9px] border text-[13.5px]"
            style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
          >
            <option value="">Unassigned</option>
            {assignee && !known && <option value={assignee}>{assignee}</option>}
            {members.map(m => (
              <option key={m.email} value={m.email}>{m.full_name || m.email}{m.email === myEmail ? ' (you)' : ''}</option>
            ))}
          </select>
          {!assignee && myEmail && (
            <button type="button" disabled={busy} onClick={() => onUpdate({ assigned_to: myEmail })} className="h-[38px] px-2.5 text-[13px] whitespace-nowrap" style={{ color: 'var(--ink-link)' }}>Assign to me</button>
          )}
        </div>

        {/* What fired */}
        <div className="flex flex-col gap-1.5">
          <Label>WHAT FIRED</Label>
          {ruleId ? (
            <Link to="/rules" className="text-[13.5px] font-medium hover:underline" style={{ color: 'var(--ink-text)' }}>
              {rule?.name || 'Detection rule'} <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{ruleId}</span>
            </Link>
          ) : (
            <span className="text-[13.5px] font-medium">{f.source === 'detection' ? 'Detection engine' : `Source: ${f.source || 'unknown'}`}</span>
          )}
          {(f.description || rule?.description) && (
            <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{f.description || rule.description}</span>
          )}
        </div>

        {/* Evidence */}
        <div className="flex flex-col gap-1.5">
          <Label right={(
            <button type="button" onClick={() => setRaw(v => !v)} aria-pressed={raw} className="h-[26px] px-2 rounded-md border font-mono text-[10.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
              {raw ? 'Readable' : 'Raw JSON'}
            </button>
          )}>EVIDENCE</Label>
          {raw ? (
            <pre className="m-0 px-3 py-2.5 rounded-[10px] border font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap break-all" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{JSON.stringify(evidence, null, 2)}</pre>
          ) : evRows.length ? (
            <div className="flex flex-col rounded-[10px] border overflow-hidden" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              {evRows.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[120px_minmax(0,1fr)] gap-2.5 px-3 py-[7px] border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                  <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{k}</span>
                  <span className="font-mono text-xs break-all">{v}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>No evidence recorded.</span>
          )}
        </div>

        {hints.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label>COULD BE BENIGN IF</Label>
            <ul className="m-0 pl-4 flex flex-col gap-1 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
              {hints.map(h => <li key={h}>{h}</li>)}
            </ul>
          </div>
        )}

        {mitre.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label>MITRE ATT&amp;CK</Label>
            <div className="flex flex-wrap gap-1.5">
              {mitre.map(id => (
                <a key={id} href={mitreUrl(id)} target="_blank" rel="noreferrer" className="h-7 inline-flex items-center gap-2 px-2.5 rounded-[7px] border text-xs hover:border-[color:var(--surf-line-strong)]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                  <span className="font-mono" style={{ color: 'var(--ink-link)' }}>{id}</span>{MITRE[id] || ''}
                </a>
              ))}
            </div>
          </div>
        )}

        {corr.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label>CORRELATED ALERTS</Label>
            {corr.map(a => (
              <Link key={`${a.type}-${a.id}`} to={`/security-alerts?alert=${a.type}-${a.id}`} className="flex items-center gap-2.5 px-3 py-2 rounded-[9px] border text-[12.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                <span className="font-mono text-[10px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{String(a.type).toUpperCase()}</span>
                <span className="flex-grow min-w-0 truncate">{a.title || `Alert #${a.id}`}</span>
              </Link>
            ))}
          </div>
        )}

        {/* Notes */}
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => setNotesOpen(v => !v)} aria-expanded={notesOpen} className={`h-7 flex items-center justify-between ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>
            <span>NOTES · {notes.length}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${notesOpen ? 'rotate-180' : ''}`} strokeWidth={1.8} />
          </button>
          {notesOpen && (
            <>
              {notes.map((n, i) => (
                <div key={i} className="flex flex-col gap-1 px-3 py-[9px] rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                  <span className="flex justify-between gap-3 text-xs">
                    <span className="font-semibold truncate">{n.who || n.email || 'Note'}</span>
                    {n.at && <span className="font-mono text-[11px] shrink-0" style={{ color: 'var(--ink-faint)' }} title={n.at}>{clock(n.at)}</span>}
                  </span>
                  <span className="text-[12.5px] leading-normal whitespace-pre-wrap" style={{ color: 'var(--ink-muted)' }}>{n.text}</span>
                </div>
              ))}
              <label htmlFor="finding-note" className="sr-only">Add a note</label>
              <div className="flex gap-2 items-end">
                <textarea
                  id="finding-note"
                  rows={2}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote(); }}
                  placeholder="Add a note for the team…"
                  className="flex-grow min-w-0 box-border px-2.5 py-2 rounded-[9px] border text-[13px] resize-none outline-none focus:border-[color:var(--accent)]"
                  style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
                />
                <button type="button" onClick={addNote} disabled={busy || !draft.trim()} className="h-[38px] px-3 rounded-[9px] border text-[13px] disabled:opacity-50" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Add</button>
              </div>
            </>
          )}
        </div>
      </div>

      {linkOpen && (
        <CaseLinker
          finding={f}
          onClose={() => setLinkOpen(false)}
          onNewCase={() => { setLinkOpen(false); onNewCase(); }}
          onLinked={(c) => { setLinkOpen(false); onNotice(`Linked to “${c?.title || 'case'}”.`); }}
        />
      )}

      <div className="flex items-center gap-2 px-[22px] py-3.5 border-t shrink-0" style={{ borderColor: 'var(--surf-line)' }}>
        <button type="button" disabled={busy} onClick={() => onUpdate({ status: nextStatus })} className={primaryBtn} style={primaryBtnStyle}>{nextLabel}</button>
        <button type="button" onClick={() => setLinkOpen(v => !v)} aria-expanded={linkOpen} className={secondaryBtn} style={secondaryBtnStyle}>
          <Link2 className="w-[15px] h-[15px]" strokeWidth={1.8} />Link to case
        </button>
        {f.status !== 'contained' && f.status !== 'resolved' && (
          <button type="button" disabled={busy} onClick={() => onUpdate({ status: 'resolved' })} className="ml-auto h-11 px-2.5 text-sm disabled:opacity-50" style={{ color: 'var(--ink-muted)' }}>Resolve</button>
        )}
      </div>
    </aside>
  );
};


export default FindingDetail;
